import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export type SellwildBannerSize = "320x50" | "300x250" | "728x90" | "300x600" | "160x600";

export interface SellwildConfigureOptions {
  partnerCode: string;
  /**
   * Optional in SDK 1.3.0+. When omitted, the native SDK derives the
   * listings URL from the remote config or a partnerCode-based default.
   */
  listingsUrl?: string;
  apiBaseUrl?: string;
  slug?: string;
  name?: string;
  appStoreUrl?: string;
  gamTag?: string;
  gptProxyUrl?: string;
  bannerZid?: string;
  bottomBannerZid?: string;
  mobileBannerZid?: string;
  mobileZids?: string[];
  hideBannerTop?: boolean;
  hideBannerBottom?: boolean;
  disableGpt?: boolean;
  adRefreshMax?: number;
  adRefreshMaxMobile?: number;
  adRefreshInterval?: number;
  prebidSrc?: string;
  widgetJsUrl?: string;
  debug?: boolean;
}

export interface SellwildShowBannerOptions {
  id: string;
  zoneId?: string;
  size?: SellwildBannerSize;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SellwildListingDTO {
  id: string;
  status: string;
  title: string;
  text?: string;
  url?: string;
  price?: string;
  currency?: string;
  photos?: Array<{ url: string; thumbUrl: string }>;
  user?: { id: string; username: string; trustLevel: string };
}

export interface SellwildTrackingStatus {
  status: "authorized" | "denied" | "restricted" | "notDetermined";
  idfa?: string;
}

export interface SellwildPlugin {
  configure(opts: SellwildConfigureOptions): Promise<{ bundleId: string }>;
  showBanner(opts: SellwildShowBannerOptions): Promise<{ id: string }>;
  updateBannerPosition(opts: { id: string; x: number; y: number }): Promise<void>;
  hideBanner(opts: { id: string }): Promise<void>;
  fetchListings(): Promise<{ listings: SellwildListingDTO[]; widgetCacheVersionId: string }>;
  trackEvent(opts: { event: string; action?: string; label?: string }): Promise<{ uid: string }>;
  getTrackingStatus(): Promise<SellwildTrackingStatus>;
  openSettings(): Promise<void>;
  requestTrackingAuthorization(): Promise<SellwildTrackingStatus>;
  addListener(
    eventName: "adImpression",
    cb: (event: { id: string; zoneId: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "adClick",
    cb: (event: { id: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "adError",
    cb: (event: { id: string; error: string }) => void,
  ): Promise<PluginListenerHandle>;
}

const isNative = () => {
  if (!Capacitor.isNativePlatform()) return false;
  const platform = Capacitor.getPlatform();
  return platform === "ios" || platform === "android";
};

const noopHandle: PluginListenerHandle = { remove: async () => {} };

const webStub: SellwildPlugin = {
  configure: async () => ({ bundleId: "" }),
  showBanner: async ({ id }) => ({ id }),
  updateBannerPosition: async () => {},
  hideBanner: async () => {},
  fetchListings: async () => ({ listings: [], widgetCacheVersionId: "" }),
  trackEvent: async () => ({ uid: "" }),
  getTrackingStatus: async () => ({ status: "notDetermined" }),
  openSettings: async () => {},
  requestTrackingAuthorization: async () => ({ status: "notDetermined" }),
  addListener: async () => noopHandle,
};

export const Sellwild: SellwildPlugin = isNative()
  ? registerPlugin<SellwildPlugin>("Sellwild")
  : webStub;

export const isSellwildNative = isNative;

let configurePromise: Promise<{ bundleId: string }> | null = null;

// Deferred so callers (e.g. AdFramework's showBanner) can await config
// completion regardless of effect ordering. Resolves immediately on web.
let resolveReady: () => void;
const readyPromise: Promise<void> = new Promise((r) => {
  resolveReady = r;
});

if (!isNative()) {
  // Web has no native plugin to configure; unblock immediately.
  resolveReady!();
}

export function ensureSellwildConfigured(
  opts: SellwildConfigureOptions,
): Promise<{ bundleId: string }> {
  if (!isNative()) return Promise.resolve({ bundleId: "" });
  if (!configurePromise) {
    configurePromise = Sellwild.configure(opts)
      .then((r) => {
        resolveReady();
        return r;
      })
      .catch((err) => {
        configurePromise = null;
        throw err;
      });
  }
  return configurePromise;
}

// Resolves once the native plugin has been configured (or immediately on web).
// Use this before calling Sellwild.showBanner / fetchListings from descendants
// of <_app>, since child-first useEffect ordering means descendant effects
// run before _app's bootstrap effect resolves configure.
export function whenSellwildReady(): Promise<void> {
  return readyPromise;
}
